import React, { useState, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import ChooseAuthentication from '../../components/Profile/two-factor-authentication/choose-authentication.component.jsx';
import EmailVerify from '../../components/Profile/two-factor-authentication/email-setup.component.jsx';
import AuthenticatorSetup from '../../components/Profile/two-factor-authentication/authenticator-app-setup';
import VerifyOtpModal from '../../components/Profile/modal/verify-otp';
import { VERIFY_OTP_MODAL } from '../../components/Profile/constants.js';
import { profileAtom } from '../../components/Settings/atom/index';
import { useAtom } from 'jotai';
import { sendOtpEmail, setup2FA, getQrCodeUrl } from '../../apis/two-factor.js';
import SEO from '../../components/SEO';
import { generateSEOMetadata, dynamicPages } from '../../utils/seo';

const TwoFactorAuthentication = () => {
  const [profile] = useAtom(profileAtom);
  const [twoFAType, setTwoFAType] = useState('');
  const [modal, setModal] = useState<any>({
    isOpen: false,
    title: '',
    desc: '',
    btnText: '',
    onClick: null
  });
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [appData, setAppData] = useState<any>({
    email: '',
    twoFactorSecretKey: '',
    otpAuthUrl: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const option = useMemo(
    () => [
      {
        title: 'Email Verification',
        value: 'EMAIL_OTP',
        //@ts-ignore
        active: profile?.twoFAType === 'EMAIL_OTP'
      },
      {
        title: 'Authenticator App',
        value: 'AUTHENTICATOR_APP_OTP',
        //@ts-ignore
        active: profile?.twoFAType === 'AUTHENTICATOR_APP_OTP'
      }
    ],
    []
  );

  const handleSetup = async (selectType: string) => {
    try {
      switch (selectType) {
        case 'EMAIL_OTP':
          {
            if (profile?.isEnable2FA) {
              handleModal({
                isOpen: true,
                desc: VERIFY_OTP_MODAL.desc.app,
                onClick: (value: any) => handleVerify(value, selectType)
              });
            } else {
              const payload = {
                twoFactorAuthType: selectType
              };
              await setup2FA(payload);
              setTwoFAType(selectType);
            }
          }
          break;
        case 'AUTHENTICATOR_APP_OTP':
          {
            if (profile?.isEnable2FA) {
              const payload = {
                email: profile.email
              };
              await sendOtpEmail(payload);
              handleModal({
                isOpen: true,
                onClick: (value: any) => handleVerify(value, selectType)
              });
            } else {
              const payload = {
                twoFactorAuthType: selectType
              };
              const response = await setup2FA(payload);
              if (response?.success) {
                const info = response.data;
                setAppData({
                  email: info.email,
                  twoFactorSecretKey: info.twoFactorSecretKey,
                  otpAuthUrl: info.otpAuthUrl
                });
              } else {
                throw new Error('Can not retrieve Authenticator App data');
              }
              setTwoFAType(selectType);
            }
          }
          break;
      }
    } catch (e) {
      setErrorMessage(VERIFY_OTP_MODAL.errorMessage);
    }
  };

  const handleVerify = async (otp: string, type: string) => {
    try {
      setIsLoading(true);
      const payload = {
        twoFactorAuthType: type,
        otp: otp
      };
      const response = await setup2FA(payload);
      if (response?.success) {
        if (type === 'AUTHENTICATOR_APP_OTP') {
          const info = response.data;
          setAppData({
            email: info.email,
            twoFactorSecretKey: info.twoFactorSecretKey,
            otpAuthUrl: info.otpAuthUrl
          });
        }
      } else {
        throw new Error('Can not retrieve Authenticator App data');
      }
      setTwoFAType(type);
      handleModal();
    } catch (e) {
      setErrorMessage(VERIFY_OTP_MODAL.errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenView = (selectType: any) => {
    if (selectType === 'AUTHENTICATOR_APP_OTP') {
      handleModal({
        isOpen: true,
        desc: VERIFY_OTP_MODAL.desc.app,
        onClick: (value: any) => handleVerifyView(value)
      });
    }
  };

  const handleVerifyView = async (otp: string) => {
    try {
      setIsLoading(true);
      const payload = {
        otp: otp
      };
      const { data }: any = await getQrCodeUrl(payload);
      if (data.success) {
        const info: any = data.data;
        setAppData({
          email: info.email,
          twoFactorSecretKey: info.twoFactorSecretKey,
          otpAuthUrl: info.otpAuthUrl
        });
      }
      setTwoFAType('AUTHENTICATOR_APP_OTP');
      handleModal();
    } catch (e) {
      setErrorMessage(VERIFY_OTP_MODAL.errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModal = (value?: any) => {
    setModal({
      isOpen: value?.isOpen || false,
      title: value?.title,
      desc: value?.desc,
      btnText: value?.btnText,
      onClick: value?.onClick || null
    });
    setErrorMessage('');
  };

  const handleCloseModal = () => {
    handleModal();
  };

  // Generate SEO metadata for two-factor authentication page
  const seoMetadata = generateSEOMetadata.dynamicPage(
    dynamicPages['account-settings/two-factor-authentication'].name,
    dynamicPages['account-settings/two-factor-authentication'].url,
    dynamicPages['account-settings/two-factor-authentication'].description
  );

  return (
    <div className="py-[30px] w-full 2xl:py-8 h-full max-w-[1308px] mx-auto px-4 md:px-[40px]">
      <SEO {...seoMetadata} />
      <div>
        {!twoFAType && (
          <ChooseAuthentication option={option} onOpenView={handleOpenView} onSetup={handleSetup} />
        )}
        {twoFAType === 'EMAIL_OTP' && <EmailVerify />}
        {twoFAType === 'AUTHENTICATOR_APP_OTP' && <AuthenticatorSetup appData={appData} />}
      </div>
      {modal?.isOpen && (
        <VerifyOtpModal
          isOpen={modal?.isOpen}
          title={modal?.title}
          desc={modal?.desc}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          onClick={(value: any) => {
            modal?.onClick(value);
          }}
          onClose={handleCloseModal}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default TwoFactorAuthentication;
